import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ImagePlus, LogOut, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { BrandMark, BrandName } from "@/components/BrandMark";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { DatePickerField } from "@/components/DatePickerField";
import { SelectField } from "@/components/SelectField";

const CATEGORIES = ["domestic", "international", "visa"];
const LIST_FIELDS = ["highlights", "inclusions", "exclusions", "terms", "payment_policy", "cancellation_policy"];
const CSV_FIELDS = ["themes", "states", "countries"];

const emptyForm = {
  title: "", category: "domestic", destination: "", duration: "", price: "", rating: 4.5,
  tag: "", image: "", overview: "", tour_type: "", continent: "",
  highlights: "", inclusions: "", exclusions: "", terms: "", payment_policy: "", cancellation_policy: "",
  themes: "", states: "", countries: "",
  itinerary: [{ day: 1, title: "", description: "" }],
  departures: [],
};

const toForm = (p) => ({
  ...emptyForm,
  ...Object.fromEntries(LIST_FIELDS.map((f) => [f, (p[f] || []).join("\n")])),
  ...Object.fromEntries(CSV_FIELDS.map((f) => [f, (p[f] || []).join(", ")])),
  title: p.title || "", category: p.category || "domestic", destination: p.destination || "",
  duration: p.duration || "", price: p.price ?? "", rating: p.rating ?? 4.5, tag: p.tag || "",
  image: p.image || "", overview: p.overview || "", tour_type: p.tour_type || "", continent: p.continent || "",
  itinerary: (p.itinerary?.length ? p.itinerary : [{ day: 1, title: "", description: "" }]).map((s) => ({ ...s })),
  departures: (p.departures || []).map((d) => ({ ...d })),
});

const toPayload = (f) => ({
  title: f.title.trim(), category: f.category, destination: f.destination.trim(),
  duration: f.duration.trim(), price: Number(f.price) || 0, rating: Number(f.rating) || 4.5,
  tag: f.tag.trim(), image: f.image.trim(), overview: f.overview.trim(),
  tour_type: f.tour_type.trim(), continent: f.continent.trim(),
  ...Object.fromEntries(LIST_FIELDS.map((k) => [k, f[k].split("\n").map((s) => s.trim()).filter(Boolean)])),
  ...Object.fromEntries(CSV_FIELDS.map((k) => [k, f[k].split(",").map((s) => s.trim()).filter(Boolean)])),
  itinerary: f.itinerary.filter((s) => s.title.trim()).map((s) => ({ day: s.day, title: s.title.trim(), description: s.description.trim() })),
  departures: f.departures
    .filter((d) => d.date)
    .map((d) => ({ date: d.date, seats_total: Number(d.seats_total) || 30, seats_left: Number(d.seats_left) || 0 })),
});

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showCount, setShowCount] = useState(80);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("arihant_admin_token");
  const role = localStorage.getItem("arihant_admin_role") || "admin";
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPackages = useCallback(async () => {
    try {
      const res = await api.get("/admin/packages", { headers });
      setPackages(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("arihant_admin_token");
        localStorage.removeItem("arihant_admin_role");
        navigate("/admin/login");
      } else if (err.response?.status === 403) {
        toast.error("Admin role required for this action.");
      } else toast.error("Could not load packages.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    fetchPackages();
  }, [token, navigate, fetchPackages]);

  const tourTypes = useMemo(
    () => [...new Set(packages.map((p) => p.tour_type).filter(Boolean))].sort(),
    [packages]
  );

  const filtered = useMemo(
    () =>
      packages.filter((p) => {
        if (catFilter !== "all" && p.category !== catFilter) return false;
        if (typeFilter !== "all" && (p.tour_type || "") !== typeFilter) return false;
        if (search && !`${p.title} ${p.destination}`.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    [packages, catFilter, typeFilter, search]
  );

  const openCreate = () => { setForm(emptyForm); setImageFile(null); setImagePreview(null); setEditing("new"); };
  const openEdit = (p) => { setForm(toForm(p)); setImageFile(null); setImagePreview(null); setEditing(p.id); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (imageFile) {
        const data = new FormData();
        data.append("file", imageFile);
        const res = await api.post("/admin/packages/upload-image", data, { headers });
        payload.image = res.data.url;
      }
      if (editing === "new") {
        await api.post("/admin/packages", payload, { headers });
        toast.success("Package created — live on the site.");
      } else {
        await api.put(`/admin/packages/${editing}`, payload, { headers });
        toast.success("Package updated.");
      }
      setEditing(null);
      fetchPackages();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed — check required fields.");
    } finally {
      setSaving(false);
    }
  };

  const toggleHidden = async (p) => {
    try {
      await api.patch(`/admin/packages/${p.id}/visibility`, { hidden: !p.hidden }, { headers });
      setPackages((prev) => prev.map((x) => (x.id === p.id ? { ...x, hidden: !p.hidden } : x)));
      toast.success(p.hidden ? `"${p.title}" is now visible.` : `"${p.title}" is now hidden from the site.`);
    } catch {
      toast.error("Could not update visibility.");
    }
  };

  const logout = () => {
    localStorage.removeItem("arihant_admin_token");
    localStorage.removeItem("arihant_admin_role");
    navigate("/admin/login");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setStep = (i, k) => (e) => {
    const itinerary = [...form.itinerary];
    itinerary[i] = { ...itinerary[i], [k]: e.target.value };
    setForm({ ...form, itinerary });
  };
  const setDep = (i, k) => (e) => {
    const departures = [...form.departures];
    departures[i] = { ...departures[i], [k]: e.target.value };
    setForm({ ...form, departures });
  };

  return (
    <div className="bg-ink min-h-screen text-cream" data-testid="admin-packages-page">
      <header className="border-b border-gold/15 bg-surface/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-packages-logo">
            <BrandMark size={36} iconSize={16} />
            <span className="font-display text-xl text-cream">
              <BrandName />
              <span className="text-xs font-mono tracking-widest text-faded ml-3 uppercase">{role}</span>
            </span>
          </Link>
          <nav className="flex items-center gap-3 flex-wrap">
            <Link to="/admin" data-testid="admin-tab-enquiries" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">Enquiries</Link>
            <span data-testid="admin-tab-packages" className="text-xs text-ink bg-gold px-5 py-2.5 rounded-full font-semibold">Packages</span>
            {role === "admin" && (
              <>
                <Link to="/admin/users" data-testid="admin-tab-users" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">Users</Link>
                <Link to="/admin/settings" data-testid="admin-tab-settings" className="text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">Site Settings</Link>
              </>
            )}
            <button onClick={logout} data-testid="admin-packages-logout-btn" className="flex items-center gap-2 text-xs text-mist border border-cream/20 px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-3">Package Manager</p>
            <h1 className="font-display text-3xl sm:text-4xl text-cream">Packages</h1>
            <p className="text-sm text-faded mt-2">{packages.length} total · {packages.filter((p) => p.hidden).length} hidden</p>
          </div>
          <button onClick={openCreate} data-testid="add-package-btn" className="flex items-center gap-2 bg-gold text-ink text-sm font-semibold px-6 py-3.5 rounded-full hover:bg-gold-hover transition-colors">
            <Plus size={16} /> Add Package
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-faded" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-package-search"
              className="field-input pl-11"
              placeholder="Search by title or destination…"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex gap-2 flex-wrap">
              {["all", ...CATEGORIES].map((c) => (
                <button
                  key={c}
                  onClick={() => setCatFilter(c)}
                  data-testid={`admin-cat-${c}`}
                  className={`text-xs font-mono tracking-widest uppercase px-5 py-2.5 rounded-full border transition-colors ${
                    catFilter === c ? "bg-gold text-ink border-gold" : "border-cream/20 text-mist hover:border-gold/50"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="sm:w-60">
              <SelectField
                value={typeFilter}
                onChange={setTypeFilter}
                testid="admin-type-filter"
                placeholder="All Tour Types"
                options={[{ value: "all", label: "All Tour Types" }, ...tourTypes.map((t) => ({ value: t, label: t }))]}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-2xl bg-card/60 animate-pulse" />)}</div>
        ) : (
          <div className="space-y-3" data-testid="admin-package-list">
            {filtered.slice(0, showCount).map((p) => (
              <div
                key={p.id}
                data-testid={`admin-package-row-${p.id}`}
                className={`rounded-2xl border bg-card/60 p-4 flex items-center gap-4 ${p.hidden ? "border-cream/10 opacity-55" : "border-gold/15"}`}
              >
                <img src={p.image} alt="" loading="lazy" className="w-16 h-16 rounded-xl object-cover shrink-0 hidden sm:block" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-lg text-cream truncate">{p.title}</p>
                    {p.hidden && <span className="text-[10px] font-mono tracking-widest uppercase text-red-300 border border-red-400/40 px-2 py-0.5 rounded-full">Hidden</span>}
                  </div>
                  <p className="text-xs text-faded truncate">{p.destination} · {p.duration} · {p.tour_type || p.category}</p>
                </div>
                <p className="font-display text-lg text-gold shrink-0 hidden md:block">{formatINR(p.price)}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => toggleHidden(p)}
                    data-testid={`toggle-visibility-${p.id}`}
                    className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center text-mist hover:border-gold hover:text-gold transition-colors"
                    title={p.hidden ? "Show on site" : "Hide from site"}
                  >
                    {p.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`edit-package-${p.id}`}
                    className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center text-mist hover:border-gold hover:text-gold transition-colors"
                    title="Edit package"
                  >
                    <Pencil size={15} />
                  </button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-center text-faded text-sm py-16">No packages match.</p>}
            {filtered.length > showCount && (
              <button
                type="button"
                onClick={() => setShowCount((n) => n + 80)}
                data-testid="admin-packages-show-more"
                className="w-full text-center text-xs font-mono tracking-widest uppercase text-mist border border-cream/20 rounded-full py-3 hover:border-gold hover:text-gold transition-colors"
              >
                Show more ({filtered.length - showCount} remaining)
              </button>
            )}
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-[70] bg-ink/90 backdrop-blur-sm overflow-y-auto" data-testid="package-editor-modal">
          <div className="max-w-3xl mx-auto my-10 px-4">
            <form onSubmit={save} className="rounded-3xl border border-gold/25 bg-surface p-6 sm:p-10 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl text-cream">{editing === "new" ? "Add Package" : "Edit Package"}</h2>
                <button type="button" onClick={() => setEditing(null)} data-testid="editor-close-btn" className="text-mist hover:text-gold"><X size={22} /></button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Title *</label>
                  <input required value={form.title} onChange={set("title")} data-testid="pkg-title-input" className="field-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Category *</label>
                  <SelectField value={form.category} onChange={(v) => set("category")({ target: { value: v } })} testid="pkg-category-select" options={CATEGORIES.map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))} />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Tour Type</label>
                  <input value={form.tour_type} onChange={set("tour_type")} data-testid="pkg-tour-type-input" className="field-input" placeholder="Group Tour / Private Van Tour" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Destination</label>
                  <input value={form.destination} onChange={set("destination")} data-testid="pkg-destination-input" className="field-input" placeholder="Jaipur · Udaipur" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Duration</label>
                  <input value={form.duration} onChange={set("duration")} data-testid="pkg-duration-input" className="field-input" placeholder="5 Nights / 6 Days" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Price (₹) *</label>
                  <input required type="number" min="0" value={form.price} onChange={set("price")} data-testid="pkg-price-input" className="field-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Rating (0–5)</label>
                  <input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={set("rating")} data-testid="pkg-rating-input" className="field-input" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Tag</label>
                  <input value={form.tag} onChange={set("tag")} data-testid="pkg-tag-input" className="field-input" placeholder="Bestseller" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Package Image</label>
                  <div className="flex items-start gap-4">
                    <div className="w-28 h-20 rounded-xl border border-dashed border-gold/40 bg-ink/60 flex items-center justify-center overflow-hidden shrink-0" data-testid="pkg-image-preview">
                      {imagePreview || form.image ? (
                        <img src={imagePreview || form.image} alt="Package" className="w-full h-full object-cover" />
                      ) : (
                        <ImagePlus size={20} className="text-gold/60" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2.5">
                      <label className="cursor-pointer inline-flex items-center gap-2 text-xs font-semibold text-gold border border-gold/40 px-4 py-2.5 rounded-full hover:bg-gold/10 transition-colors">
                        <ImagePlus size={14} /> Upload from device
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          data-testid="pkg-image-file-input"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
                          }}
                        />
                      </label>
                      <input value={form.image} onChange={set("image")} data-testid="pkg-image-input" className="field-input" placeholder="…or paste an image URL" />
                      {imageFile && <p className="text-[11px] text-faded">Will upload on save: {imageFile.name}</p>}
                    </div>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Overview</label>
                  <textarea rows={2} value={form.overview} onChange={set("overview")} data-testid="pkg-overview-input" className="field-input resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Themes (comma separated)</label>
                  <input value={form.themes} onChange={set("themes")} data-testid="pkg-themes-input" className="field-input" placeholder="Hills, Honeymoon" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">States (domestic, comma separated)</label>
                  <input value={form.states} onChange={set("states")} data-testid="pkg-states-input" className="field-input" placeholder="Rajasthan, Gujarat" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Continent (international)</label>
                  <input value={form.continent} onChange={set("continent")} data-testid="pkg-continent-input" className="field-input" placeholder="Europe" />
                </div>
                <div>
                  <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">Countries (comma separated)</label>
                  <input value={form.countries} onChange={set("countries")} data-testid="pkg-countries-input" className="field-input" placeholder="France, Switzerland" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-mono tracking-widest uppercase text-faded">Day-wise Itinerary</label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, itinerary: [...form.itinerary, { day: form.itinerary.length + 1, title: "", description: "" }] })}
                    data-testid="add-itinerary-step-btn"
                    className="text-xs text-gold flex items-center gap-1 hover:text-gold-hover"
                  >
                    <Plus size={13} /> Add day
                  </button>
                </div>
                <div className="space-y-3">
                  {form.itinerary.map((step, i) => (
                    <div key={i} className="grid grid-cols-[64px_1fr_2fr_32px] gap-2 items-center">
                      <input value={step.day} onChange={setStep(i, "day")} className="field-input text-center" placeholder="Day" />
                      <input value={step.title} onChange={setStep(i, "title")} className="field-input" placeholder="Title" data-testid={`itinerary-title-${i}`} />
                      <input value={step.description} onChange={setStep(i, "description")} className="field-input" placeholder="Description" />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, itinerary: form.itinerary.filter((_, j) => j !== i) })}
                        className="text-faded hover:text-red-400"
                        aria-label="Remove day"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-mono tracking-widest uppercase text-faded">Fixed Departures (date · total seats · seats left)</label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, departures: [...form.departures, { date: "", seats_total: 30, seats_left: 30 }] })}
                    data-testid="add-departure-btn"
                    className="text-xs text-gold flex items-center gap-1 hover:text-gold-hover"
                  >
                    <Plus size={13} /> Add date
                  </button>
                </div>
                {form.departures.length === 0 ? (
                  <p className="text-[11px] text-faded">No fixed departures — leave empty for private/flexible tours.</p>
                ) : (
                  <div className="space-y-3">
                    {form.departures.map((d, i) => (
                      <div key={i} className="grid grid-cols-[1fr_90px_90px_32px] gap-2 items-center">
                        <DatePickerField value={d.date} onChange={(v) => setDep(i, "date")({ target: { value: v } })} testid={`departure-date-${i}`} placeholder="Pick date" />
                        <input type="number" min="0" value={d.seats_total} onChange={setDep(i, "seats_total")} className="field-input" placeholder="Total" />
                        <input type="number" min="0" value={d.seats_left} onChange={setDep(i, "seats_left")} className="field-input" placeholder="Left" data-testid={`departure-left-${i}`} />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, departures: form.departures.filter((_, j) => j !== i) })}
                          className="text-faded hover:text-red-400"
                          aria-label="Remove departure"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  ["highlights", "Highlights (one per line)"],
                  ["inclusions", "Inclusive (one per line)"],
                  ["exclusions", "Exclusive (one per line)"],
                  ["terms", "Terms & Conditions (one per line)"],
                  ["payment_policy", "Payment Policy (one per line)"],
                  ["cancellation_policy", "Cancellation Policy (one per line)"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-xs font-mono tracking-widest uppercase text-faded mb-2">{label}</label>
                    <textarea rows={4} value={form[key]} onChange={set(key)} data-testid={`pkg-${key}-input`} className="field-input resize-none" />
                  </div>
                ))}
              </div>

              <button type="submit" disabled={saving} data-testid="pkg-save-btn" className="bg-gold text-ink font-semibold text-sm px-10 py-4 rounded-full hover:bg-gold-hover transition-colors disabled:opacity-60">
                {saving ? "Saving…" : editing === "new" ? "Create Package" : "Save Changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
